import React from "react";

import DropzoneIcon from "@common/assets/images/dropzone-icon.png";
import Paragraph from "@common/components/Paragraph/Paragraph";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { Attachment, SupportedFileTypes } from "@common/models";
import { vars } from "@common/styles";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { docLink } from "@common/util/docs";
import { resolveFileType } from "@common/util/files";
import identifiers from "@common/util/identifiers.json";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import * as Styled from "@main/pages/shared/components/Attachments/AttachmentsContainer.styles";
import { ImageAttachment } from "@main/pages/shared/components/Attachments/ImageAttachment/ImageAttachment";
import { UnsupportedAttachment } from "@main/pages/shared/components/Attachments/UnsupportedAttachment/UnsupportedAttachment";
import VideoAttachment from "@main/pages/shared/components/Attachments/VideoAttachment/VideoAttachment";
import { CSSProperties } from "styled-components";

interface Props {
  attachments: Attachment[];
  style?: CSSProperties;
  width?: number;
  height?: number;
  deviceOrientation?: string;
  onSelectFileClick?: () => void;
  onRemoveClick?: (attachment: Attachment) => void;
}

export default function AttachmentsContainer({
  attachments = [],
  style,
  width,
  height,
  deviceOrientation,
  onSelectFileClick,
  onRemoveClick,
}: Props) {
  const { selectedApp } = useAppSelectionContext();

  const getCardOrientation = (attachment: Attachment) => {
    if (attachment.isSelectFile) return "landscape";

    const fileType: SupportedFileTypes | undefined = attachment.type
      ? attachment.type
      : resolveFileType(attachment.url);
    if (fileType === SupportedFileTypes.TXT) return "landscape";
    if (fileType === SupportedFileTypes.VIDEO || fileType === SupportedFileTypes.IMAGE) {
      return deviceOrientation?.toLowerCase().includes("portrait") ? "portrait" : "landscape";
    }

    return "";
  };

  const renderAttachment = (attachment: Attachment) => {
    if (attachment.url) return renderAttachmentByFileType(attachment, style, width, height);
    if (attachment.isHelper) return renderAutoAttachBox();
    if (attachment.isSelectFile) return renderSelectFileBox();
  };

  const renderAttachmentByFileType = (
    attachment: Attachment,
    style?: CSSProperties,
    width?: number,
    height?: number,
  ) => {
    const fileType: SupportedFileTypes | undefined = attachment.type
      ? attachment.type
      : resolveFileType(attachment.url);
    switch (fileType) {
      case SupportedFileTypes.IMAGE:
        return (
          <ImageAttachment
            style={style}
            attachment={attachment}
            onRemoveClick={onRemoveClick}
          />
        );
      case SupportedFileTypes.VIDEO:
        return (
          <VideoAttachment
            style={style}
            attachment={attachment}
            onRemoveClick={onRemoveClick}
            width={width}
            height={height}
          />
        );
      default:
        return (
          <UnsupportedAttachment
            style={style}
            attachment={attachment}
            onRemoveClick={onRemoveClick}
          />
        );
    }
  };

  const renderAutoAttachBox = () => {
    return (
      <Styled.Helper>
        <Tooltip
          text="Auto attach files"
          position="top"
        >
          <a
            href={docLink(selectedApp?.platform.name).autoAttach}
            target="_blank"
            rel="noreferrer"
          >
            <Styled.Plus />
          </a>
        </Tooltip>
      </Styled.Helper>
    );
  };

  const renderSelectFileBox = () => {
    return (
      <Styled.SelectFile onClick={onSelectFileClick}>
        <img
          alt={"Select file icon"}
          width={64}
          height={64}
          src={DropzoneIcon}
        />
        <Flex $column>
          <Paragraph
            style={{ textAlign: "start" }}
            color={vars.colors.grey40}
            p3
          >
            {"Drag a file anywhere or click here to upload"}
          </Paragraph>
          <Paragraph
            style={{ textAlign: "start", marginTop: "0.4rem" }}
            p3
          >
            {"Max file size 10MB"}
          </Paragraph>
        </Flex>
      </Styled.SelectFile>
    );
  };

  return (
    <Styled.MainContainer style={style}>
      <Styled.Grid>
        {attachments.map((attachment, i) => {
          return (
            <Styled.Card
              data-testid={identifiers["module.attachment"]}
              key={attachment.url ?? i}
              $type={attachment.url ? resolveFileType(attachment.url) : attachment.type}
              $orientation={getCardOrientation(attachment)}
              $isEmpty={!attachment.url}
              $isHelper={attachment.isHelper}
              style={style}
            >
              {renderAttachment(attachment)}
            </Styled.Card>
          );
        })}
      </Styled.Grid>
    </Styled.MainContainer>
  );
}
